/* eslint-disable @typescript-eslint/no-empty-object-type */
import { captureException } from '@sentry/react';

import { CalendarContext } from './calendarContext';

const baseUrl = import.meta.env.VITE_API_URL;

export type ErrorWrapper<TError> = TError | { payload: string; status: number };

/**
 * The `{ payload, status }` shape callers read, but as a real Error.
 *
 * Thrown as a plain object, any rejection that went unhandled reached Sentry
 * through the browser's global handler as "Object captured as promise
 * rejection with keys: payload, status" -- no name, no stack, every endpoint
 * in one bucket. Observed on production as TELECAL-WEB-APP-14F, still there
 * after #161 removed the explicit double-capture.
 *
 * `payload` and `status` stay enumerable own properties, so everything that
 * reads them keeps working: GoogleAuthPage reads `status`, the two Google
 * account hooks read `payload`, and the catch below tests for `status` with
 * hasOwnProperty.
 */
export class ApiError<TError = unknown> extends Error {
  payload: string | TError;
  status: number;

  constructor(
    method: string,
    url: string,
    status: number,
    payload: string | TError,
  ) {
    super(`API ${status} for ${method.toUpperCase()} ${url}`);
    this.name = 'ApiError';
    this.payload = payload;
    this.status = status;
  }
}

export type CalendarFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams,
  TWithCode,
> = CalendarContext['fetcherOptions'] & {
  body?: TBody;
  headers?: THeaders;
  method: string;
  pathParams?: TPathParams;
  queryParams?: TQueryParams;
  signal?: AbortSignal;
  url: string;
  withCode?: TWithCode;
};

export async function calendarFetch<
  TData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  TError,
  TBody extends FormData | {} | null | undefined,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
  TWithCode extends boolean = false,
>({
  body,
  headers,
  method,
  pathParams,
  queryParams,
  signal,
  url,
  withCode,
}: CalendarFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams,
  TWithCode
>): Promise<TWithCode extends true ? { data: TData; status: number } : TData>;
export async function calendarFetch<
  TData,
  TError,
  TBody extends FormData | {} | null | undefined,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  body,
  headers,
  method,
  pathParams,
  queryParams,
  signal,
  url,
  withCode,
}: CalendarFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams,
  boolean
>): Promise<TData | { data: TData; status: number }> {
  try {
    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...headers,
    };

    /**
     * As the fetch API is being used, when multipart/form-data is specified
     * the Content-Type header must be deleted so that the browser can set
     * the correct boundary.
     * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
     */
    if (
      requestHeaders['Content-Type']
        .toLowerCase()
        .includes('multipart/form-data')
    ) {
      delete requestHeaders['Content-Type'];
    }

    const resolvedUrl = resolveUrl(url, queryParams, pathParams);

    const response = await window.fetch(`${baseUrl}${resolvedUrl}`, {
      body: body
        ? body instanceof FormData
          ? body
          : JSON.stringify(body)
        : undefined,
      headers: requestHeaders,
      method: method.toUpperCase(),
      signal,
    });
    if (!response.ok) {
      let payload: unknown;
      if (response.status === 500) {
        payload = 'Unexpected error';
      } else {
        try {
          payload = await response.json();
        } catch (e) {
          payload =
            e instanceof Error
              ? `Unexpected error (${e.message})`
              : 'Unexpected error';
        }
      }

      const error = new ApiError(method, url, response.status, payload);

      // Report only what somebody can act on. A 4xx is this API working as
      // designed -- an expired draft, a validation failure, an unauthorised
      // read -- and reporting every one of them is what made this project
      // unreadable and got Sentry switched off in cbf9376.
      //
      // The thrown value keeps its `{ payload, status }` shape either way:
      // GoogleAuthPage reads `error.status`, and the two Google account hooks
      // read `error.payload`.
      if (response.status >= 500) {
        captureException(error, {
          // Sentry groups by stack trace, and every Error here is built on
          // the same line, so without an explicit fingerprint every endpoint
          // collapses into one issue whose title floats to whichever event
          // arrived last. Measured on production 2026-09-04: a single issue
          // held both GET /event_drafts/{id} and GET /slot.
          //
          // Fingerprint on `url`, the template, and not on `resolvedUrl` --
          // the resolved one carries record ids, which would swing the
          // problem the other way and open a new issue per id.
          extra: { payload, url: resolvedUrl },
          fingerprint: [
            'api-error',
            method.toUpperCase(),
            url,
            String(response.status),
          ],
        });
      }

      throw error;
    }

    if (response.headers.get('content-type')?.includes('json')) {
      const data = (await response.json()) as TData;
      if (withCode) {
        return { data, status: response.status };
      }
      return data;
    } else {
      // if it is not a json response, assume it is a blob and cast it to TData
      const data = (await response.blob()) as unknown as TData;
      if (withCode) {
        return { data, status: response.status };
      }
      return data;
    }
  } catch (e) {
    // Anything carrying `status` came from the branch above, which has already
    // decided whether it is worth reporting. Capturing it again here is what
    // reported every failed request twice -- and because a plain object has no
    // name or stack, Sentry filed all of them under one unnamed `calendarFetch`
    // issue rather than grouping them by endpoint.
    if (Object.prototype.hasOwnProperty.call(e, 'status')) {
      throw e;
    }

    // A cancelled request is not a failure. TanStack Query aborts in-flight
    // queries on unmount and on refetch, so reporting these would recreate
    // exactly the noise this change exists to remove. Rethrowing the original
    // abort also lets Query recognise the cancellation, which replacing it
    // with a synthesised Error below used to prevent.
    if (
      signal?.aborted ||
      (e instanceof DOMException && e.name === 'AbortError')
    ) {
      throw e;
    }

    // A genuine transport failure: no response at all. Worth reporting, once,
    // as a real Error so it carries a stack and groups sensibly.
    const networkError = new Error(
      e instanceof Error ? `Network error (${e.message})` : 'Network error',
    );

    captureException(networkError);
    throw networkError;
  }
}

const resolveUrl = (
  url: string,
  queryParams: Record<string, string> = {},
  pathParams: Record<string, string> = {},
) => {
  // By default, URLSearchParams serializes an array as key=val1,val2,val3,
  // but our backend expects key[]=val1,val2,val3.
  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(`${key}[]`, item);
      });
    } else {
      searchParams.set(key, value);
    }
  });
  let query = searchParams.toString();
  if (query) {
    query = `?${query}`;
  }
  return url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query;
};
